import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { getSessionId, trackEvent } from '../lib/track';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONVO_KEY = 'nbl_chat_id';
const NAME_KEY = 'nbl_chat_name';

const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [convoId, setConvoId] = useState(localStorage.getItem(CONVO_KEY) || '');
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || '');
  const [contact, setContact] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('active');
  const bottomRef = useRef(null);

  const fetchMessages = async () => {
    if (!convoId) return;
    try {
      const res = await fetch(`${API}/chat/${convoId}/messages`);
      if (res.ok) setMessages(await res.json());
      const c = await fetch(`${API}/chat/${convoId}`);
      if (c.ok) setStatus((await c.json()).status);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (!open || !convoId) return;
    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, convoId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const start = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), name: name.trim(), contact: contact.trim(), page: pathname }),
      });
      const convo = await res.json();
      localStorage.setItem(CONVO_KEY, convo.id);
      localStorage.setItem(NAME_KEY, name.trim());
      setConvoId(convo.id);
      setStatus(convo.status);
      trackEvent('chat_start');
    } finally {
      setSending(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !convoId) return;
    setDraft('');
    setSending(true);
    try {
      const res = await fetch(`${API}/chat/${convoId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const msg = await res.json();
      setMessages((m) => [...m, msg]);
      trackEvent('chat_message');
    } finally {
      setSending(false);
    }
  };

  const openChat = () => {
    setOpen(true);
    trackEvent('chat_open');
  };

  return (
    <div data-testid="chat-widget" className="fixed bottom-[72px] right-4 z-50 sm:bottom-6 sm:right-6">
      {!open && (
        <button
          data-testid="chat-open-button"
          onClick={openChat}
          aria-label="Open live chat"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-bronze text-paper shadow-[0_12px_30px_-8px_rgba(11,61,11,0.5)] transition-transform hover:scale-105"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div className="flex h-[480px] w-[min(92vw,360px)] flex-col overflow-hidden rounded-lg border border-line bg-paper shadow-[0_30px_80px_-20px_rgba(22,22,21,0.45)]">
          <div className="flex items-center justify-between bg-graphite px-4 py-3.5 text-paper">
            <div>
              <p className="font-serif text-lg font-medium leading-none">Chat with us</p>
              <p className="mt-1 text-[11px] text-paper/60">Nebraska Backyard Living · replies in real time</p>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Close chat" className="p-1 text-paper/70 hover:text-paper">
              <X className="h-5 w-5" />
            </button>
          </div>

          {!convoId ? (
            <form onSubmit={start} className="flex flex-1 flex-col justify-center gap-3 p-5" data-testid="chat-start-form">
              <p className="text-sm text-muted2">Hi! Tell us who you are and we'll be right with you.</p>
              <input data-testid="chat-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name *" className="input-field !py-3" />
              <input data-testid="chat-contact-input" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Email or phone (optional)" className="input-field !py-3" />
              <button type="submit" data-testid="chat-start-button" disabled={sending} className="btn-primary !py-3">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Chat'}
              </button>
            </form>
          ) : (
            <>
              <div data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto bg-paper2/40 p-4">
                {messages.length === 0 && (
                  <p className="py-8 text-center text-sm text-muted2">Say hello — we're here to help with your project.</p>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.sender === 'visitor' ? 'bg-bronze text-paper' : 'border border-line bg-paper text-ink'
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {status === 'resolved' ? (
                <p className="border-t border-line px-4 py-3 text-center text-xs text-muted2">This conversation was marked resolved. <button data-testid="chat-new-button" className="font-semibold text-bronze underline" onClick={() => { localStorage.removeItem(CONVO_KEY); setConvoId(''); setMessages([]); }}>Start a new chat</button></p>
              ) : (
                <form onSubmit={send} className="flex items-center gap-2 border-t border-line p-3">
                  <input data-testid="chat-message-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type a message…" className="input-field !border-0 !bg-transparent !py-2.5" />
                  <button type="submit" data-testid="chat-send-button" disabled={sending || !draft.trim()} aria-label="Send message" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bronze text-paper disabled:opacity-40">
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
